import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, input, Input, OnChanges, Output } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ChatActions } from 'libs/shared/data/stores/chat';
import { MenuItem } from 'primeng/api';
import { ConfirmationService, ConfirmEventType, MessageService } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';

import { ProfileMiniComponent } from '@offconon/shared/ui/profile-mini';
import { TruncatePipe } from '@offconon/shared/utils/pipes';
import { AppHelperService } from '@offconon/shared/utils/services/app-helper';

@Component({
  selector: 'offconon-chat-room-display',
  imports: [
    CommonModule,
    AvatarModule,
    MatIcon,
    TruncatePipe,
    TooltipModule,
    TranslateModule,
    ButtonModule,
    MenuModule,
    RouterModule,
    ConfirmDialogModule,
    ProfileMiniComponent,
  ],
  templateUrl: './chat-room-display.component.html',
  styles: ``,
  providers: [ConfirmationService],
})
export class ChatRoomDisplayComponent implements OnChanges {
  room = input<any>();
  @Input() logged_user: any;
  @Input() selectedBusiness: any;
  @Input() parameters: any;
  @Input() platform = 'core';
  @Input() truncate_unread = 20;
  @Output() selectedChatRoomId = new EventEmitter<number>();

  private router = inject(Router);
  private translateService = inject(TranslateService);
  private appHelperService = inject(AppHelperService);

  private store = inject(Store);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);

  dialogVisible = false;
  items: MenuItem[];

  second_line: any;
  partner_data: any;
  members: any[] = [];

  viewer_room_profile: any;

  profile_link = '/';

  online_show = false;

  full_name: any;
  avatar_path: any;
  is_online: any;
  username: any;
  is_subscription = false;
  is_verified = false;
  subscription_icon: any;
  subscription_color: any;
  subscription_name: any;

  profile_mini_parameters: any = {};

  last_msg: any;
  unread_count: any;

  ngOnChanges(): void {
    this.full_name = this.room()?.name;
    this.second_line = this.room()?.room_type;
    this.unread_count = this.room()?.unread_count;

    this.last_msg = this.room()?.last_message?.message;

    if (this.room()?.members) {
      this.members = this.room()?.members.map((member: any) => {
        return {
          member: member,
          user: member?.user,
          co_worker: member?.co_worker,
          business: member?.business,
          mute: member?.mute,
          room_member_id: member?.id,
          permission: member?.permission,
        };
      });

      if (this.room()?.room_type === 'direct' || this.room()?.room_type === 'app') {
        if (this.platform === 'core') {
          this.partner_data = this.members?.find((data: any) => {
            return data?.user?.id !== this.logged_user?.id;
          });
        } else {
          this.partner_data = this.members?.find((data: any) => {
            return data?.co_worker?.co_worker_id !== this.selectedBusiness?.business_co_worker_id;
          });
        }
      }

      if (this.platform === 'core') {
        if (this.partner_data?.user) {
          this.online_show = true;
          this.full_name = this.partner_data?.user?.full_name;
          this.avatar_path = this.partner_data?.user?.avatar_path;
          this.is_online = this.partner_data?.user?.is_online;
          this.username = this.partner_data?.user?.username;

          this.is_subscription = this.partner_data?.user?.is_subscription;
          this.is_verified = this.partner_data?.user?.is_verified;
          this.subscription_icon = this.partner_data?.user?.subscription_icon;
          this.subscription_color = this.partner_data?.user?.subscription_icon_color;
          this.subscription_name = this.partner_data?.user?.subscription_type;

          this.profile_link = '/profile/' + this.partner_data?.user?.username;
          this.second_line = this.translateService.instant('Private room');

          if (this.room()?.room_type === 'app') {
            this.second_line = this.appHelperService.getAppData(this.room()?.community_type)[
              'app_brand_name'
            ];

            this.profile_link =
              this.appHelperService.getAppData(this.room()?.community_type)['app_url'] +
              '/member-profile/' +
              this.username;
          }
        }

        if (this.partner_data?.business) {
          this.online_show = true;
          this.full_name = this.partner_data?.business?.business_name;
          this.avatar_path = this.partner_data?.business?.business_avatar_path;
          this.is_online = this.partner_data?.business?.business_is_online;
          this.username = this.partner_data?.business?.business_mention_name;

          this.is_subscription = this.partner_data?.business?.is_subscription;
          this.is_verified = this.partner_data?.business?.business_is_verified;
          this.subscription_icon = this.partner_data?.business?.subscription_icon;
          this.subscription_color = this.partner_data?.business?.subscription_icon_color;
          this.subscription_name = this.partner_data?.business?.subscription_type;

          this.profile_link =
            '/business-profile/' + this.partner_data?.business?.business_mention_name;
          this.second_line = this.translateService.instant('Private room');
        }
      } else {
        if (this.partner_data?.co_worker) {
          this.online_show = true;
          this.full_name = this.partner_data?.co_worker?.full_name;
          this.avatar_path = this.partner_data?.co_worker?.avatar_path;
          this.is_online = this.partner_data?.co_worker?.is_online;
          this.username = this.partner_data?.co_worker?.username;

          this.profile_link = '/co-workers/profile/' + this.partner_data?.co_worker?.co_worker_id;
          this.second_line = this.translateService.instant('Private room');
        }
      }

      this.profile_mini_parameters = {
        is_online_show: this.online_show,
        full_name: this.full_name,
        avatar_path: this.avatar_path,
        is_online: this.is_online,
        is_subscription: this.is_subscription,
        is_verified: this.is_verified,
        subscription_icon: this.subscription_icon,
        subscription_color: this.subscription_color,
        subscription_name: this.subscription_name,
      };

      if (this.room()?.room_type === 'group') {
        this.second_line = this.translateService.instant('Group room');
      }

      if (this.room()?.room_type === 'wg_project') {
        this.second_line = this.translateService.instant('Workgroup / Project');
      }

      this.viewer_room_profile = this.members?.find((data: any) => {
        return data?.user?.id === this.logged_user?.id;
      });
    }

    if (this.room() && this.viewer_room_profile) {
      this.renderMenu(this.room(), this.profile_link, this.viewer_room_profile);
    }
  }

  renderMenu(data: any, profile_link: any, viewer_room_profile: any) {
    this.items = [
      {
        label: this.translateService.instant('Options'),
        visible: true,
        items: [
          {
            label: this.translateService.instant('Profile'),
            icon: 'account_box',
            visible: data?.room_type === 'direct' || data?.room_type === 'app' ? true : false,
            command: () => {
              this.openSubPage(profile_link);
            },
          },
          {
            label: this.translateService.instant('Open in App'),
            icon: 'settings_overscan',
            visible: this.parameters?.view_type === 'widget',
            command: () => {
              this.openInApp();
            },
          },
          {
            label: this.translateService.instant('Audio call'),
            icon: 'call',
            visible: true,
            command: () => {
              this.startCall();
            },
          },
          {
            label: this.translateService.instant('Video call'),
            icon: 'videocam',
            visible: true,
            command: () => {
              this.startCall();
            },
          },
        ],
      },
      {
        label: this.translateService.instant('Settings'),
        visible: true,
        items: [
          {
            label: viewer_room_profile?.mute
              ? this.translateService.instant('Unmute notifications')
              : this.translateService.instant('Mute notifications'),
            icon: viewer_room_profile?.mute ? 'notifications_off' : 'notifications',
            visible: true,
            icon_color: viewer_room_profile?.mute ? 'text-red-500' : '',
            command: () => {
              this.mute(data, viewer_room_profile);
            },
          },

          {
            label: this.translateService.instant('Archive chat'),
            icon: 'archive',
            visible: true,
            command: () => {
              // this.openSubPage(profile_link);
            },
          },

          {
            label: this.translateService.instant('Delete'),
            icon: 'delete',
            icon_color: 'text-red-500',
            visible: data?.room_type === 'direct',
            command: () => {
              this.deleteRoom(data.id);
            },
          },

          {
            label: this.translateService.instant('Delete group'),
            icon: 'delete',
            icon_color: 'text-red-500',
            visible:
              data?.room_type === 'group' && viewer_room_profile?.permission === 'admin'
                ? true
                : false,
            command: () => {
              this.deleteRoom(data.id);
            },
          },

          {
            label: this.translateService.instant('Leave chat'),
            icon: 'logout',
            icon_color: 'text-red-500',
            visible:
              data?.room_type === 'group' && viewer_room_profile?.permission === 'member'
                ? true
                : false,
            command: () => {
              this.leaveRoom(viewer_room_profile?.room_member_id);
            },
          },
        ],
      },
    ];
  }

  deleteRoom(id: number) {
    this.dialogVisible = true;
    setTimeout(() => {
      this.confirmationService.confirm({
        message:
          this.translateService.instant('Are you sure you want to delete this?') +
          ' ' +
          this.translateService.instant('This decision can not be reversed!'),
        header: this.translateService.instant('Delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.store.dispatch(ChatActions.deleteRoom({ id }));
        },
        reject: (type: any) => {
          switch (type) {
            case ConfirmEventType.REJECT:
            case ConfirmEventType.CANCEL:
              this.messageService.add({
                severity: 'warn',
                summary: this.translateService.instant('Cancel'),
                detail: this.translateService.instant('This item has remained active!'),
              });
              break;
          }
        },
        key: 'chatroomDeleteDialog',
      });
    }, 500);
  }

  leaveRoom(id: number) {
    this.dialogVisible = true;
    setTimeout(() => {
      this.confirmationService.confirm({
        message: this.translateService.instant('Are you sure you want to leave this chat?'),
        header: this.translateService.instant('Leave chat'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.store.dispatch(ChatActions.leaveRoom({ roomId: id }));
        },

        key: 'chatroomLeaveDialog',
      });
    }, 500);
  }
  selectRoom(id: number) {
    if (window.location.href.includes('communication')) {
      this.router.navigate(['communication', id]);
    } else {
      this.store.dispatch(ChatActions.selectRoom({ id }));
      this.selectedChatRoomId.emit(id);
    }
  }

  openSubPage(url: any) {
    this.router.navigate([url]);
  }

  startCall() {
    this.router.navigate(['/communication/' + this.room()?.id + '/call']);
  }

  openInApp() {
    this.router.navigate(['/communication/' + this.room()?.id]);
  }

  mute(data: any, viewer_room_profile: any) {
    let payload: any = {};

    payload = {
      mute: !viewer_room_profile?.mute,
      room_id: data?.id,
    };
    this.store.dispatch(ChatActions.updateRoomMember({ roomMember: payload }));
  }

  /*
  invite() {
    if (!this.selectedRoomId) return;

    if (this.selectedUser) {
      this.store.dispatch(
        inviteUsersToRoom({
          roomId: this.selectedRoomId(),
          userIds: this.selectedUser.id as number,
        }),
      );
    }
  }*/
}
